/**
 * Fixed asset/liability type catalog. Each entry drives icon + color + grouping.
 */
export const ASSET_TYPES = [
  { id: 'cash', label: 'Cash', icon: '💵', color: '#22c55e' },
  { id: 'savings', label: 'Savings Account', icon: '🏦', color: '#3b82f6' },
  { id: 'super', label: 'Superannuation', icon: '🛟', color: '#8b5cf6' },
  { id: 'property', label: 'Property', icon: '🏠', color: '#f97316' },
  { id: 'vehicle', label: 'Vehicle', icon: '🚗', color: '#06b6d4' },
  { id: 'investment', label: 'Investment', icon: '📈', color: '#ec4899' },
  { id: 'other', label: 'Other Asset', icon: '💼', color: '#64748b' },
];

export const LIABILITY_TYPES = [
  { id: 'mortgage', label: 'Mortgage', icon: '🏚️', color: '#ef4444' },
  { id: 'credit-card', label: 'Credit Card', icon: '💳', color: '#f97316' },
  { id: 'personal-loan', label: 'Personal Loan', icon: '📄', color: '#eab308' },
  { id: 'car-loan', label: 'Car Loan', icon: '🚙', color: '#a855f7' },
  { id: 'hecs', label: 'HECS/HELP', icon: '🎓', color: '#0ea5e9' },
  { id: 'other', label: 'Other Liability', icon: '⚠️', color: '#64748b' },
];

export const SAVINGS_VIRTUAL_ASSET_ID = 'savings-goals-virtual';

export function assetTypeMeta(typeId) {
  return ASSET_TYPES.find((t) => t.id === typeId) || ASSET_TYPES[ASSET_TYPES.length - 1];
}

export function liabilityTypeMeta(typeId) {
  return LIABILITY_TYPES.find((t) => t.id === typeId) || LIABILITY_TYPES[LIABILITY_TYPES.length - 1];
}

/**
 * Lazy shape guard. Writes happen after next user action; reads adapt to legacy/missing shape.
 */
export function migrateNetWorth(netWorth) {
  if (!netWorth || typeof netWorth !== 'object') {
    return { assets: {}, liabilities: {}, history: {} };
  }
  return {
    assets: netWorth.assets && typeof netWorth.assets === 'object' ? netWorth.assets : {},
    liabilities: netWorth.liabilities && typeof netWorth.liabilities === 'object' ? netWorth.liabilities : {},
    history: netWorth.history && typeof netWorth.history === 'object' ? netWorth.history : {},
  };
}

export function sumSavingsGoals(savingsGoalsMap) {
  if (!savingsGoalsMap || typeof savingsGoalsMap !== 'object') return 0;
  return Object.values(savingsGoalsMap).reduce((s, g) => s + (Number(g?.saved) || 0), 0);
}

/**
 * Read-only virtual asset row synthesised from savings goals. Never stored.
 * Returns null when no goals exist.
 */
export function buildSavingsGoalsVirtualAsset(savingsGoalsMap) {
  const total = sumSavingsGoals(savingsGoalsMap);
  const goalCount = savingsGoalsMap ? Object.keys(savingsGoalsMap).length : 0;
  if (goalCount === 0) return null;
  return {
    id: SAVINGS_VIRTUAL_ASSET_ID,
    type: 'savings',
    name: 'Savings Goals (' + goalCount + ')',
    value: total,
    notes: 'Auto-synced from Savings Goals page',
    readOnly: true,
    virtual: true,
  };
}

/**
 * Compute totals + entries list (real assets + virtual savings-goals row).
 */
export function computeTotals(netWorth, savingsGoalsMap) {
  const assets = Object.values(netWorth.assets || {}).map((a) => ({ ...a, value: Number(a.value) || 0 }));
  const liabilities = Object.values(netWorth.liabilities || {}).map((l) => ({ ...l, balance: Number(l.balance) || 0 }));

  const virtualSavings = buildSavingsGoalsVirtualAsset(savingsGoalsMap);
  const assetsAll = virtualSavings ? [virtualSavings, ...assets] : assets;

  const totalRealAssets = assets.reduce((s, a) => s + a.value, 0);
  const totalSavingsGoals = virtualSavings ? virtualSavings.value : 0;
  const totalAssets = totalRealAssets + totalSavingsGoals;
  const totalLiabilities = liabilities.reduce((s, l) => s + l.balance, 0);
  const netWorthValue = totalAssets - totalLiabilities;

  return {
    assets: assetsAll,
    liabilities,
    totalAssets,
    totalLiabilities,
    netWorth: netWorthValue,
    totalSavingsGoals,
  };
}

/**
 * UTC YYYY-MM key so trend math stays timezone-safe (matches bill recurrence convention).
 */
export function currentMonthKey(date = new Date()) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return y + '-' + m;
}

/**
 * Returns true if history for the given month is missing OR values drifted from current totals.
 */
export function snapshotNeedsUpdate(history, monthKey, totals) {
  const existing = history && history[monthKey];
  if (!existing) return true;
  return (
    Number(existing.totalAssets) !== Number(totals.totalAssets) ||
    Number(existing.totalLiabilities) !== Number(totals.totalLiabilities) ||
    Number(existing.netWorth) !== Number(totals.netWorth)
  );
}

/**
 * Sort history keys ascending, return [{monthKey, ...}] for chart consumption.
 */
export function historySeries(history) {
  if (!history || typeof history !== 'object') return [];
  return Object.entries(history)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([monthKey, snap]) => ({
      monthKey,
      totalAssets: Number(snap?.totalAssets) || 0,
      totalLiabilities: Number(snap?.totalLiabilities) || 0,
      netWorth: Number(snap?.netWorth) || 0,
      capturedAt: snap?.capturedAt || null,
    }));
}
